import React from 'react';
import { FormattedMessage } from 'react-intl';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import BackIcon from '@mui/icons-material/ArrowBack';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CachedIcon from '@mui/icons-material/Cached';
import SchoolIcon from '@mui/icons-material/School';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import SwapIcon from '@mui/icons-material/SwapHoriz';
import PhoneIcon from '@mui/icons-material/Phone';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BugReportIcon from '@mui/icons-material/BugReport';
import moment from 'moment';
import Spacings from '~/modules/ui-kit/spacings';

import ContentView from '~/components/ContentView';
import { useAuditEvents } from '~/hooks/api/useAuditEvents';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material';

const eventIconsMap = {
  PasswordResetRequested: VpnKeyIcon,
  PasswordReset: VpnKeyIcon,
  PublisherCreated: PersonAddIcon,
  InvitationCreated: InsertInvitationIcon,
  PublisherAccountChanged: EditIcon,
  PublisherAccountDeactivated: BlockIcon,
  PublisherAcceptedInvitation: HowToRegIcon,
  PublisherAccountLocked: LockIcon,
  PublisherCongregationChanged: CachedIcon,
  PublisherExperienceChanged: SchoolIcon,
  PublisherLinkToPartnerChanged: PersonIcon,
  PublisherNameChanged: PersonIcon,
  PublisherPrivilegeChanged: LoyaltyIcon,
  PioneerLevelChanged: SwapIcon,
  PublisherPhoneChanged: PhoneIcon,
  PublisherConsentToErrorTrackingChanged: BugReportIcon,
  PublisherConsentToJwPhoneChanged: PhoneIcon,
  Unsupported: EventNoteIcon,
} as Record<string, OverridableComponent<SvgIconTypeMap<{}, 'svg'>>>;

type AuditEventProps = {
  auditEvent: {
    event: string;
    eventTime: string;
    causedBy: string;
    eventDetails: Record<string, boolean>;
  };
};

// TODO extract
const AuditEvent = (props: AuditEventProps) => {
  const Icon =
    eventIconsMap[props.auditEvent.event] || eventIconsMap.Unsupported;
  const message = messages[props.auditEvent.event as keyof typeof messages];
  return (
    <Spacings.Inset className={styles.event}>
      <Spacings.Stack scale="l">
        <Spacings.Inline alignItems="center" scale="l">
          <Icon color="action" />
          <div>
            <Typography variant="subtitle1">
              {message ? (
                <FormattedMessage {...message} />
              ) : (
                props.auditEvent.event
              )}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {moment(props.auditEvent.eventTime).format('L LT')} &bull;{' '}
              {props.auditEvent.causedBy}
            </Typography>
          </div>
        </Spacings.Inline>
        {Object.entries(props.auditEvent.eventDetails).length > 0 ? (
          <Spacings.Stack className={styles.details}>
            {Object.entries(props.auditEvent.eventDetails).map(
              ([key, value]) => (
                <div key={key}>
                  <Typography
                    color={
                      typeof value === 'boolean' || value
                        ? 'textPrimary'
                        : 'textSecondary'
                    }
                  >
                    {typeof value === 'boolean'
                      ? value.toString()
                      : value || <FormattedMessage {...messages.noValue} />}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {key}
                  </Typography>
                </div>
              )
            )}
          </Spacings.Stack>
        ) : null}
      </Spacings.Stack>
    </Spacings.Inset>
  );
};

const route = getRouteApi('/users/$publisherId/audit');

const AuditEventList = () => {
  const params = route.useParams();
  const navigate = useNavigate();
  const search = route.useSearch();
  const { data: auditEvents, isLoading } = useAuditEvents(params.publisherId);

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Spacings.Inline alignItems="center">
              <IconButton
                color="inherit"
                onClick={() => {
                  navigate({
                    to: search.from || `/users/${params.publisherId}`,
                  });
                }}
                size="large"
              >
                <BackIcon data-testid="back" />
              </IconButton>
              <div>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.header} />
                </Typography>
              </div>
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={isLoading}
    >
      {!isLoading && auditEvents && auditEvents.length === 0 ? (
        <FormattedMessage {...messages.noAuditEventsYet} />
      ) : null}
      {auditEvents?.map((auditEvent, index) => (
        <React.Fragment key={auditEvent.id}>
          <AuditEvent
            auditEvent={auditEvent as AuditEventProps['auditEvent']}
          />
          {auditEvents.length > index + 1 && <Divider variant="inset" />}
        </React.Fragment>
      ))}
    </ContentView>
  );
};

export default AuditEventList;
